import { SendOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Table, Tag, Tooltip, Typography } from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { compareText } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  InvitableCongregationRow,
  ListResponse,
  NotificationsResponse,
  OtherCircumscriptionRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";

/**
 * Sidebar entry "invite_congregations" → /circumscription/invite_congregations.
 *
 * Asks a congregation of another circumscription to move into the caller's own.
 * Needs full access, not just an observer's rights: the invitation goes out by
 * mail to the congregation's local coordinators and to the zone coordinators of
 * *both* circumscriptions, and the congregation decides.
 *
 * Master/detail as in the old application: pick a circumscription above, see
 * its congregations below. A congregation that has already been asked is
 * marked — the invitation stays open until it is answered.
 */
export function InviteCongregationsPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [circumscriptionId, setCircumscriptionId] = useState<number | null>(null);

  const circumscriptions = useQuery({
    queryKey: ["circumscription", "other_circumscriptions"],
    queryFn: () =>
      apiFetch<ListResponse<OtherCircumscriptionRow>>("/circumscription/other_circumscriptions"),
  });

  const congregations = useQuery({
    queryKey: ["circumscription", "other_congregations", circumscriptionId],
    queryFn: () =>
      apiFetch<ListResponse<InvitableCongregationRow>>(
        `/circumscription/other_circumscriptions/${circumscriptionId}/congregations`,
      ),
    enabled: circumscriptionId !== null,
  });

  const invite = useMutation({
    mutationFn: (id: number) =>
      apiFetch<NotificationsResponse>(`/circumscription/congregations/${id}/invite`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({
        queryKey: ["circumscription", "other_congregations"],
      });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
  });

  const askAndInvite = useCallback(
    (row: InvitableCongregationRow) => {
      // The old toolbar asked nothing here, but this reaches people outside the
      // caller's own circumscription by mail — worth one confirmation.
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: (
          <>
            <Typography.Paragraph>
              {[row.congregation_code, row.denomination].filter(Boolean).join(" · ")}
            </Typography.Paragraph>
            {t("congregation.invite")}
          </>
        ),
        okText: t("dialog.answer_yes"),
        cancelText: t("dialog.answer_no"),
        onOk: () => invite.mutate(row.id),
      });
    },
    [modal, t, invite],
  );

  const congregationColumns = useMemo<TableProps<InvitableCongregationRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
        sorter: (a, b) => compareText(a.congregation_code, b.congregation_code),
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
        defaultSortOrder: "ascend",
        sorter: (a, b) => compareText(a.denomination, b.denomination),
      },
      {
        title: t("activerecord.attributes.congregation.invitation_pending"),
        dataIndex: "invitation_pending",
        width: 160,
        render: (value: boolean) =>
          value ? <Tag color="blue">{t("invitation.status_pending")}</Tag> : null,
      },
      {
        key: "actions",
        fixed: "right",
        width: 70,
        render: (_value, row) => (
          <Tooltip
            title={
              row.invitation_pending ? t("invitation.status_pending") : t("congregation.invite")
            }
          >
            <Button
              size="small"
              icon={<SendOutlined />}
              // One open invitation at a time; the server refuses a second one.
              disabled={invite.isPending || row.invitation_pending}
              onClick={() => askAndInvite(row)}
            />
          </Tooltip>
        ),
      },
    ],
    [t, invite.isPending, askAndInvite],
  );

  return (
    <Card title={t("navigation.invite_congregations")}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title={t("invitation.choose_circumscription")}
      />

      <Table<OtherCircumscriptionRow>
        rowKey="id"
        size="small"
        pagination={false}
        scroll={{ y: 240 }}
        loading={circumscriptions.isPending}
        dataSource={circumscriptions.data?.data ?? []}
        columns={[
          {
            title: t("activerecord.attributes.circumscription.circumscription_code"),
            dataIndex: "circumscription_code",
            width: 90,
          },
          {
            title: t("activerecord.attributes.circumscription.denomination"),
            dataIndex: "denomination",
            ellipsis: true,
          },
        ]}
        rowClassName={(row) => (row.id === circumscriptionId ? "ant-table-row-selected" : "")}
        onRow={(row) => ({
          onClick: () => setCircumscriptionId(row.id),
          style: { cursor: "pointer" },
        })}
      />

      {circumscriptionId !== null && (
        <>
          <Typography.Title level={5} style={{ marginTop: 24 }}>
            {t("activerecord.models.congregation")}
          </Typography.Title>

          <DataTable<InvitableCongregationRow>
            columns={congregationColumns}
            rows={congregations.data?.data}
            exportAs={t("navigation.invite_congregations")}
            loading={congregations.isPending || invite.isPending}
            error={congregations.error}
          />
        </>
      )}
    </Card>
  );
}
